<div class="appointment-formgroup-setup-container">
  <!-- Header -->
  <div class="setup-header">
    <h2>Appointment Type - Form Group Assignments</h2>
    <p class="subtitle">Assign form groups to appointment types. When a patient books an appointment, forms in the assigned group will be sent automatically.</p>
  </div>

  <!-- Filters -->
  <div class="filters-section">
    <div class="filter-row">
      <div class="search-box">
        <input
          type="text"
          [(ngModel)]="filterText"
          (ngModelChange)="onFilterChange()"
          placeholder="Search appointment types..."
          class="form-control"
        />
      </div>
      <div class="checkbox-filter">
        <label>
          <input
            type="checkbox"
            [(ngModel)]="showOnlyWithForms"
            (ngModelChange)="onFilterChange()"
          />
          Show only types with forms assigned
        </label>
      </div>
    </div>
  </div>

  <!-- Loading State -->
  <div *ngIf="isLoading" class="loading-container">
    <div class="spinner"></div>
    <p>Loading...</p>
  </div>

  <!-- Main Table -->
  <div *ngIf="!isLoading && ViewPermission"  class="table-container">
    <table class="data-table">
      <thead>
        <tr>
          <th>Appointment Type</th>
          <th>Description</th>
          <th>Assigned Form Group</th>
          <th>Status</th>
          <th>Actions</th>
        </tr>
      </thead>
      <tbody>
        <tr *ngFor="let appType of paginatedAppointmentTypes">
          <td class="type-name">{{ appType.typeName }}</td>
          <td class="type-desc">{{ appType.typeDesc || '-' }}</td>
          <td class="group-name">
            <span *ngIf="appType.hasFormGroup" class="badge badge-success">
              {{ appType.groupName }}
            </span>
            <span *ngIf="!appType.hasFormGroup" class="badge badge-empty">
              No forms assigned
            </span>
          </td>
          <td class="status">
            <span
              *ngIf="appType.hasFormGroup"
              class="status-indicator active"
              title="Forms will be sent when this appointment type is booked"
            >
              ●
            </span>
            <span
              *ngIf="!appType.hasFormGroup"
              class="status-indicator inactive"
              title="No forms configured"
            >
              ○
            </span>
          </td>
          <td class="actions">
            <button *ngIf="InsertPermission"
              class="btn btn-primary btn-sm"
              (click)="openAssignModal(appType)"
              title="{{ appType.hasFormGroup ? 'Change form group' : 'Assign form group' }}"
            >
              {{ appType.hasFormGroup ? 'Change' : 'Assign' }}
            </button>
            <button 
              *ngIf="appType.hasFormGroup && DeletePermission"
              class="btn btn-danger btn-sm"
              (click)="openDeleteModal(appType)"
              title="Remove form group assignment"
            >
              Remove
            </button>
          </td>
        </tr>
        <tr *ngIf="paginatedAppointmentTypes.length === 0">
          <td colspan="5" class="text-center no-data">
            <p *ngIf="!filterText && !showOnlyWithForms">No appointment types found</p>
            <p *ngIf="filterText || showOnlyWithForms">No appointment types match your filters</p>
          </td>
        </tr>
      </tbody>
    </table>
  </div>

  <!-- Pagination -->
  <div *ngIf="!isLoading && totalPages > 1" class="pagination">
    <button
      class="btn btn-secondary"
      [disabled]="currentPage === 1"
      (click)="previousPage()"
    >
      Previous
    </button>
    <span class="page-info">
      Page {{ currentPage }} of {{ totalPages }}
      ({{ filteredAppointmentTypes.length }} total)
    </span>
    <button
      class="btn btn-secondary"
      [disabled]="currentPage === totalPages"
      (click)="nextPage()"
    >
      Next
    </button>
  </div>

  <!-- Assign/Change Modal -->
  <div *ngIf="showAssignModal" class="modal-overlay" (click)="closeAssignModal()">
    <div class="modal-content" (click)="$event.stopPropagation()">
      <div class="modal-header">
        <h3>{{ currentAppointmentType?.hasFormGroup ? 'Change' : 'Assign' }} Form Group</h3>
        <button class="close-btn" (click)="closeAssignModal()">&times;</button>
      </div>
      <div class="modal-body">
        <div class="form-group">
          <label for="appointmentTypeName">Appointment Type:</label>
          <input
            type="text"
            id="appointmentTypeName"
            [value]="currentAppointmentType?.typeName"
            readonly
            class="form-control readonly"
          />
        </div>

        <div class="form-group">
          <label for="formGroupSelect">Form Group: *</label>
          <select
            id="formGroupSelect"
            [(ngModel)]="selectedGroupName"
            class="form-control"
            [disabled]="!hasFormGroups"
          >
            <option value="">-- Select a form group --</option>
            <option *ngFor="let group of formGroups" [value]="group">
              {{ group }}
            </option>
          </select>
          <small *ngIf="!hasFormGroups" class="text-danger">
            No form groups available. Please create form groups first in the Form Group Setup page.
          </small>
        </div>

        <!-- NEW: Policy Selection -->
        <div class="form-group">
          <label class="checkbox-label">
            <input
              type="checkbox"
              [(ngModel)]="selectedRequireForEachAppointment"
            />
            <span>Require forms for each appointment</span>
          </label>
          <small class="help-text">
            <strong>If checked:</strong> Forms will be sent every time a patient books this appointment type, even if they've completed them before.
            <br>
            <strong>If unchecked:</strong> Forms will only be sent if the patient has never completed them.
          </small>
        </div>

        <div class="info-box">
          <strong>📋 When to check "Require for each appointment":</strong>
          <ul>
            <li>Pre-visit questionnaires (needs current info)</li>
            <li>Appointment-specific intake forms</li>
            <li>Time-sensitive health screenings</li>
          </ul>
          <strong>When to leave unchecked:</strong>
          <ul>
            <li>HIPAA consent (one-time only)</li>
            <li>General consent forms</li>
            <li>Policies (return policy, no-show, etc.)</li>
          </ul>
        </div>
      </div>
      <div class="modal-footer">
        <button
          class="btn btn-secondary"
          (click)="closeAssignModal()"
          [disabled]="isSaving"
        >
          Cancel
        </button>
        <button
          class="btn btn-primary"
          (click)="saveMapping()"
          [disabled]="isSaving || !selectedGroupName || !hasFormGroups"
        >
          {{ isSaving ? 'Saving...' : 'Save' }}
        </button>
      </div>
    </div>
  </div>

  <!-- Delete Confirmation Modal -->
  <div *ngIf="showDeleteModal" class="modal-overlay" (click)="closeDeleteModal()">
    <div class="modal-content modal-sm" (click)="$event.stopPropagation()">
      <div class="modal-header">
        <h3>Remove Form Group Assignment</h3>
        <button class="close-btn" (click)="closeDeleteModal()">&times;</button>
      </div>
      <div class="modal-body">
        <p>Are you sure you want to remove the form group assignment from:</p>
        <p class="text-bold">{{ currentAppointmentType?.typeName }}</p>
        <p class="text-muted">
          Current assignment: <span class="badge badge-warning">{{ currentAppointmentType?.groupName }}</span>
        </p>
        <div class="warning-box">
          ⚠️ After removal, no forms will be sent when this appointment type is booked.
        </div>
      </div>
      <div class="modal-footer">
        <button
          class="btn btn-secondary"
          (click)="closeDeleteModal()"
          [disabled]="isSaving"
        >
          Cancel
        </button>
        <button
          class="btn btn-danger"
          (click)="confirmDelete()"
          [disabled]="isSaving"
        >
          {{ isSaving ? 'Removing...' : 'Remove' }}
        </button>
      </div>
    </div>
  </div>
</div>
